import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const LAST_SEEN_KEY = "bf_whatsnew_lastseen";

export default function WhatsNewBell() {
  const [notes, setNotes] = useState([]);
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/release-notes");
      setNotes(data);
      const last = localStorage.getItem(LAST_SEEN_KEY);
      const lastDt = last ? new Date(last) : new Date(0);
      setUnread(data.filter((n) => new Date(n.created_at) > lastDt).length);
    } catch (err) {
      // Expected for signed-out visitors (no /release-notes session). Log at debug only.
      console.debug("release-notes fetch skipped", err?.response?.status || err?.message);
    }
  };
  useEffect(() => { load(); }, []);

  const handleOpen = (o) => {
    setOpen(o);
    if (o) {
      localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
      setUnread(0);
    }
  };

  if (notes.length === 0) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <button
          className="relative inline-flex items-center gap-1.5 px-3 h-9 rounded text-sm transition-colors hover:bg-[color:var(--bf-surface)]"
          style={{ color: "var(--bf-text-muted)" }}
          data-testid="whats-new-btn"
        >
          <Sparkles className="w-4 h-4" />
          What&apos;s new
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-[10px] font-semibold flex items-center justify-center px-1 text-white"
              style={{ background: "var(--bf-primary)" }}
              data-testid="whats-new-unread-count"
            >
              {unread}
            </span>
          )}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl max-h-[80vh] overflow-y-auto" data-testid="whats-new-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl flex items-center gap-2">
            <Sparkles className="w-6 h-6" style={{ color: "var(--bf-primary)" }} />
            What&apos;s new
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          {notes.map((n) => (
            <article
              key={n.note_id}
              className="pb-4 border-b last:border-b-0"
              style={{ borderColor: "var(--bf-border)" }}
              data-testid={`whats-new-note-${n.note_id}`}
            >
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="font-serif text-xl">{n.title}</h3>
                {n.version_tag && <Badge variant="outline" className="text-xs">{n.version_tag}</Badge>}
                {n.audience === "admin" && (
                  <Badge style={{ background: "#FEF3C7", color: "#92400E" }} className="text-xs" data-testid={`whats-new-admin-badge-${n.note_id}`}>
                    admin-only
                  </Badge>
                )}
              </div>
              <p className="text-xs mb-2" style={{ color: "var(--bf-text-muted)" }}>
                {new Date(n.created_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
              </p>
              <div
                className="prose-book max-w-none text-sm whitespace-pre-wrap"
                style={{ color: "var(--bf-text)" }}
                data-testid={`whats-new-body-${n.note_id}`}
              >
                {n.body}
              </div>
            </article>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
